import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { GridBackground } from "../EAKCArchitecture/components/GridBackground";
import { COLORS, FONTS, SPRING } from "./constants";

const STEPS = [
  { icon: "\uD83D\uDCE4", step: 1, text: "Upload your docs", color: COLORS.accentBlue, delay: 30 },
  { icon: "\uD83D\uDCAC", step: 2, text: "Ask your first question", color: COLORS.accentPurple, delay: 60 },
  { icon: "\u2705", step: 3, text: "Get answers with sources", color: COLORS.accentGreen, delay: 90 },
];

const EAKC_LETTERS = [
  { char: "E", color: COLORS.accentBlue },
  { char: "A", color: COLORS.accentPurple },
  { char: "K", color: COLORS.accentTeal },
  { char: "C", color: COLORS.accentPink },
];

export const Scene12CTA: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // --- Phase A: Three Steps (0-250) ---
  const stepsTitleProgress = spring({
    frame: frame - 10,
    fps,
    config: SPRING.snappy,
  });
  const stepsTitleOpacity = interpolate(stepsTitleProgress, [0, 1], [0, 1]);
  const stepsTitleY = interpolate(stepsTitleProgress, [0, 1], [20, 0]);

  const phaseAFade = interpolate(frame, [220, 260], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // --- Phase B: URL + Branding (250-500) ---
  const urlProgress = spring({
    frame: frame - 260,
    fps,
    config: SPRING.snappy,
  });
  const urlOpacity = interpolate(urlProgress, [0, 1], [0, 1]);

  const brandProgress = spring({
    frame: frame - 280,
    fps,
    config: SPRING.gentle,
  });
  const brandOpacity = interpolate(brandProgress, [0, 1], [0, 1]);

  // URL glow pulse
  const glowSize = interpolate(
    Math.sin(frame * 0.06),
    [-1, 1],
    [15, 35],
  );

  const phaseBFade = interpolate(frame, [500, 530], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // --- Phase C: Logo (500-750) ---
  const logoProgress = spring({
    frame: frame - 530,
    fps,
    config: SPRING.gentle,
  });
  const logoOpacity = interpolate(logoProgress, [0, 1], [0, 1]);
  const logoScale = interpolate(logoProgress, [0, 1], [0.7, 1]);

  const taglineProgress = spring({
    frame: frame - 550,
    fps,
    config: SPRING.gentle,
  });
  const taglineOpacity = interpolate(taglineProgress, [0, 1], [0, 1]);

  // Final fade to black
  const finalFade = interpolate(frame, [710, 750], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill>
      <GridBackground />

      <div style={{ opacity: finalFade }}>
        {/* Phase A: Steps Title */}
        {frame < 260 && (
          <div
            style={{
              position: "absolute",
              top: 200,
              left: 0,
              right: 0,
              textAlign: "center",
              fontFamily: FONTS.heading,
              fontSize: 36,
              fontWeight: 700,
              color: COLORS.textPrimary,
              opacity: stepsTitleOpacity * phaseAFade,
              transform: `translateY(${stepsTitleY}px)`,
            }}
          >
            Get started in 3 steps
          </div>
        )}

        {/* Phase A: Step Cards */}
        {frame < 260 && (
          <div
            style={{
              position: "absolute",
              top: 320,
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "center",
              gap: 60,
              opacity: phaseAFade,
            }}
          >
            {STEPS.map((s) => {
              const cardProgress = spring({
                frame: frame - s.delay,
                fps,
                config: SPRING.bouncy,
              });
              const cardOpacity = interpolate(cardProgress, [0, 1], [0, 1]);
              const cardY = interpolate(cardProgress, [0, 1], [30, 0]);

              return (
                <div
                  key={s.step}
                  style={{
                    width: 300,
                    height: 180,
                    background: COLORS.bgCard,
                    border: `1px solid ${COLORS.borderLight}`,
                    borderRadius: 16,
                    padding: 24,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 12,
                    opacity: cardOpacity,
                    transform: `translateY(${cardY}px)`,
                  }}
                >
                  <div style={{ fontSize: 40 }}>{s.icon}</div>
                  <div
                    style={{
                      fontFamily: FONTS.mono,
                      fontSize: 12,
                      color: s.color,
                    }}
                  >
                    Step {s.step}
                  </div>
                  <div
                    style={{
                      fontFamily: FONTS.heading,
                      fontSize: 20,
                      fontWeight: 600,
                      color: COLORS.textPrimary,
                      textAlign: "center",
                    }}
                  >
                    {s.text}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Phase B: URL + Branding */}
        {frame >= 250 && frame < 530 && (
          <>
            <div
              style={{
                position: "absolute",
                top: 400,
                left: 0,
                right: 0,
                textAlign: "center",
                fontFamily: FONTS.mono,
                fontSize: 32,
                fontWeight: 600,
                color: COLORS.accentTeal,
                opacity: urlOpacity * phaseBFade,
                textShadow: `0 0 ${glowSize}px ${COLORS.accentTeal}99`,
              }}
            >
              eakc.adaptivesmartsystems.cc
            </div>
            <div
              style={{
                position: "absolute",
                top: 460,
                left: 0,
                right: 0,
                textAlign: "center",
                fontFamily: FONTS.heading,
                fontSize: 22,
                color: COLORS.textSecondary,
                opacity: brandOpacity * phaseBFade,
              }}
            >
              Adaptive Smart Systems
            </div>
            <div
              style={{
                position: "absolute",
                top: 500,
                left: 0,
                right: 0,
                textAlign: "center",
                fontFamily: FONTS.body,
                fontSize: 18,
                color: COLORS.textMuted,
                opacity: brandOpacity * phaseBFade,
              }}
            >
              Your Company's Brain
            </div>
          </>
        )}

        {/* Phase C: EAKC Logo */}
        {frame >= 500 && (
          <>
            <div
              style={{
                position: "absolute",
                top: 380,
                left: 0,
                right: 0,
                textAlign: "center",
                opacity: logoOpacity,
                transform: `scale(${logoScale})`,
              }}
            >
              <div style={{ display: "inline-flex", gap: 8 }}>
                {EAKC_LETTERS.map((l, i) => {
                  const letterGlow = interpolate(
                    Math.sin(frame * 0.04 + i * 0.8),
                    [-1, 1],
                    [10, 25],
                  );
                  return (
                    <span
                      key={l.char}
                      style={{
                        fontFamily: FONTS.heading,
                        fontSize: 120,
                        fontWeight: 800,
                        color: l.color,
                        textShadow: `0 0 ${letterGlow}px ${l.color}80`,
                      }}
                    >
                      {l.char}
                    </span>
                  );
                })}
              </div>
            </div>

            <div
              style={{
                position: "absolute",
                top: 560,
                left: 0,
                right: 0,
                textAlign: "center",
                fontFamily: FONTS.body,
                fontSize: 24,
                color: COLORS.textSecondary,
                opacity: taglineOpacity,
              }}
            >
              Your Company's Brain
            </div>
          </>
        )}
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
